// Person-cookie terminal transport. No bearer token, arbitrary path, command,
// environment, input retry or mutation replay belongs to this client.
import { ACTING_AS, ApiError } from './client'
import { uuid4 } from './uuid'
import { releaseTerminalClient } from '../code/terminal/memory'
import type { TerminalClient, TerminalEvent, TerminalInfo, TerminalList } from '../code/terminal/types'

const MAX_PIECE = 16 * 1024
const MAX_FRAME = 24 * 1024
// Keep these bounds aligned with poulpe_protocol::terminal. The shared
// runner/server/browser fixture tests exercise the complete retained list.
const LIVE_TERMINALS = 4
const MAX_TERMINALS = 16
// Sixteen metadata records plus List context, including escaped paths.
const MAX_RESPONSE = 1024 * 1024
const SAFE = Number.MAX_SAFE_INTEGER
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const nil = '00000000-0000-0000-0000-000000000000'
const record = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const integer = (v: unknown, min = 0, max = SAFE): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= min && v <= max
const id = (v: unknown): v is string => typeof v === 'string' && UUID.test(v) && v !== nil
const text = (v: unknown, max: number): v is string => typeof v === 'string' && v.length > 0 && new TextEncoder().encode(v).length <= max && !/[\u0000-\u001f\u007f-\u009f]/u.test(v)
const keys = (v: Record<string, unknown>, allowed: string[]) => Object.keys(v).every(k => allowed.includes(k))
const bad = () => new ApiError(0, 'bad_response', 'The terminal server sent an invalid response.')
const disposed = () => new ApiError(0, 'scope_ended', 'This terminal connection belongs to an earlier sign-in or workspace.')

export type TerminalEnd = { kind: 'exited'; code: number | null; signal: number | null } | { kind: 'closed' } | { kind: 'io' }
export interface TerminalOutput { offset: number; data: Uint8Array; reset: boolean; end?: TerminalEnd }
function endOf(v: unknown): TerminalEnd | null {
  if (!record(v)) return null
  if (v.kind === 'closed' || v.kind === 'io') return keys(v, ['kind']) ? v as TerminalEnd : null
  return v.kind === 'exited' && keys(v, ['kind', 'code', 'signal']) && (v.code === null || integer(v.code, -2147483648, 2147483647)) && (v.signal === null || integer(v.signal, 1, 2147483647)) ? v as TerminalEnd : null
}
/** Canonical padded standard base64 only; check the encoded bound before decoding. */
export function terminalBytes(v: unknown): Uint8Array | null {
  if (typeof v !== 'string' || v.length > Math.ceil(MAX_PIECE / 3) * 4 || v.length % 4 !== 0 || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(v)) return null
  try {
    const binary = atob(v)
    if (binary.length > MAX_PIECE || btoa(binary) !== v) return null
    return Uint8Array.from(binary, c => c.charCodeAt(0))
  } catch { return null }
}
export function terminalOutput(value: unknown): TerminalOutput | null {
  if (!record(value) || value.kind !== 'output' || !keys(value, ['kind', 'offset', 'data', 'reset', 'end']) || !integer(value.offset) || typeof value.reset !== 'boolean') return null
  const data = terminalBytes(value.data)
  const end = value.end === undefined ? undefined : endOf(value.end)
  if (!data || value.offset > SAFE - data.length || end === null || (data.length === 0 && !value.reset && !end)) return null
  return { offset: value.offset, data, reset: value.reset, ...(end ? { end } : {}) }
}
function infoOf(v: unknown, session: string): TerminalInfo {
  if (!record(v) || !keys(v, ['id', 'session', 'instance', 'cwd', 'shell', 'size', 'created_at', 'input_seq', 'end']) || !id(v.id) || v.session !== session || !id(v.instance) || !text(v.cwd, 4096) || !text(v.shell, 4096) || !record(v.size) || !keys(v.size, ['cols', 'rows']) || !integer(v.size.cols, 1, 1000) || !integer(v.size.rows, 1, 500) || !text(v.created_at, 64) || !/^\d{4}-\d\d-\d\dT.*(?:Z|[+-]\d\d:\d\d)$/.test(v.created_at) || !Number.isFinite(Date.parse(v.created_at)) || !integer(v.input_seq, 1)) throw bad()
  const end = v.end === undefined ? undefined : endOf(v.end)
  if (end === null) throw bad()
  return { id: v.id, session, cwd: v.cwd, shell: v.shell, cols: v.size.cols, rows: v.size.rows, created_at: v.created_at, input_seq: v.input_seq, ...(end?.kind === 'exited' ? { exited: true, code: end.code } : end ? { stopped: end.kind === 'closed' ? { message: 'The terminal was closed.', gone: true } : { message: 'The terminal stopped after an I/O failure.' } } : {}) }
}
async function jsonOf(response: Response): Promise<unknown> {
  const reader = response.body?.getReader()
  if (!reader) throw bad()
  const chunks: Uint8Array[] = []
  let length = 0
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      length += value.length
      if (length > MAX_RESPONSE) { await reader.cancel(); throw bad() }
      chunks.push(value)
    }
    const bytes = new Uint8Array(length)
    let at = 0
    for (const chunk of chunks) { bytes.set(chunk, at); at += chunk.length }
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)) as unknown
  } catch { throw bad() }
  finally { reader.releaseLock() }
}

export interface TerminalBinding {
  workspace: string
  member: string
  session: string
  /** Checked before operations and delivery, even before React redraws. */
  current(): boolean
  unauthorized(): void
  online(): boolean
}
export interface LiveTerminalClient extends TerminalClient { dispose(): void }
export type TerminalClientFactory = (binding: TerminalBinding) => LiveTerminalClient
export interface TerminalTransportOptions {
  fetch?: typeof globalThis.fetch
  WebSocket?: new (url: string) => WebSocket
  page?: Pick<Location, 'protocol' | 'host'>
  uuid?: () => string
  timeoutMs?: number
  reconnectMs?: number
}

export function terminalClientFactory(options: TerminalTransportOptions = {}): TerminalClientFactory {
  return binding => new CookieTerminalClient(binding, options)
}
class CookieTerminalClient implements LiveTerminalClient {
  private readonly controllers = new Set<AbortController>()
  private readonly watches = new Set<() => void>()
  private over = false
  private readonly fetch: typeof globalThis.fetch
  private readonly Socket: new (url: string) => WebSocket
  private readonly page: Pick<Location, 'protocol' | 'host'>
  private readonly uuid: () => string
  private readonly path: string
  constructor(private readonly binding: TerminalBinding, private readonly options: TerminalTransportOptions) {
    if (![binding.workspace, binding.member, binding.session].every(id)) throw disposed()
    this.fetch = options.fetch ?? globalThis.fetch.bind(globalThis)
    this.Socket = options.WebSocket ?? globalThis.WebSocket
    this.page = options.page ?? window.location
    this.uuid = options.uuid ?? uuid4
    this.path = `/v1/sessions/${encodeURIComponent(binding.session)}/terminals`
  }
  private valid(): boolean { return !this.over && this.binding.current() }
  private check(): void {
    if (!this.valid()) throw disposed()
    if (!this.binding.online()) throw new ApiError(0, 'offline', 'The board is offline. Input was stopped.')
  }
  private terminal(idValue: string): string { this.check(); if (!id(idValue)) throw bad(); return `${this.path}/${encodeURIComponent(idValue)}` }
  private async request(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown): Promise<unknown> {
    this.check()
    const controller = new AbortController()
    this.controllers.add(controller)
    const timer = setTimeout(() => controller.abort(), this.options.timeoutMs ?? 10_000)
    try {
      let response: Response
      try {
        response = await this.fetch(path, { method, credentials: 'same-origin', headers: { Accept: 'application/json', [ACTING_AS]: this.binding.member, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) }, ...(body === undefined ? {} : { body: JSON.stringify(body) }), signal: controller.signal })
      } catch {
        if (!this.valid()) throw disposed()
        throw new ApiError(0, method === 'GET' ? 'network' : 'operation_uncertain', method === 'GET' ? 'The terminal server could not be reached.' : 'The terminal operation may have arrived. It was not sent again.')
      }
      if (!this.valid()) throw disposed()
      if (response.status === 401) this.binding.unauthorized()
      let value: unknown
      try { value = await jsonOf(response) }
      catch {
        if (!this.valid()) throw disposed()
        if (response.ok && method !== 'GET') throw new ApiError(0, 'operation_uncertain', 'The terminal reply could not be read. The operation may have arrived; it was not sent again.')
        throw bad()
      }
      if (!this.valid()) throw disposed()
      if (!response.ok) {
        if (record(value) && text(value.error, 64) && text(value.message, 512)) throw new ApiError(response.status, value.error, value.message, text(value.reason, 64) ? value.reason : undefined)
        throw new ApiError(response.status, 'terminal_refused', 'The terminal server refused the operation.')
      }
      return value
    } finally { clearTimeout(timer); this.controllers.delete(controller) }
  }
  async list(): Promise<TerminalList> {
    const v = await this.request('GET', this.path)
    if (!record(v) || v.kind !== 'listed' || !keys(v, ['kind', 'cwd', 'shell', 'terminals']) || !text(v.cwd, 4096) || !text(v.shell, 4096) || !Array.isArray(v.terminals) || v.terminals.length > MAX_TERMINALS) throw bad()
    const terminals = v.terminals.map(t => infoOf(t, this.binding.session))
    if (new Set(terminals.map(t => t.id)).size !== terminals.length || terminals.filter(t => !t.exited && !t.stopped).length > LIVE_TERMINALS) throw bad()
    return { cwd: v.cwd, shell: v.shell, terminals }
  }
  async open(session: string): Promise<TerminalInfo> {
    this.check()
    if (session !== this.binding.session) throw disposed()
    const terminal = this.uuid()
    if (!id(terminal)) throw bad()
    try {
      const v = await this.request('POST', this.path, { id: terminal, cols: 80, rows: 24 })
      if (!record(v) || v.kind !== 'opened' || !keys(v, ['kind', 'terminal'])) throw bad()
      const opened = infoOf(v.terminal, session)
      if (opened.id !== terminal) throw bad()
      return opened
    } catch (error) {
      if (error instanceof ApiError && (error.code === 'operation_uncertain' || error.code === 'bad_response')) {
        // Read-only reconciliation of this exact Open intention; never reopen it.
        try { const found = (await this.list()).terminals.find(t => t.id === terminal); if (found) return found } catch { /* The original uncertainty stays visible. */ }
        throw new ApiError(0, 'operation_uncertain', 'The terminal may have opened. Its reply could not be confirmed; it was not opened again.')
      }
      throw error
    }
  }

  async input(terminal: string, seq: number, data: string): Promise<{ input_seq: number }> {
    const path = this.terminal(terminal)
    const bytes = terminalBytes(data)
    const input = this.uuid()
    if (!integer(seq, 1, SAFE - 1) || !bytes?.length || !id(input)) throw bad()
    const v = await this.request('POST', `${path}/input`, { input, seq, data })
    if (!record(v) || v.kind !== 'input' || !keys(v, ['kind', 'input_seq']) || v.input_seq !== seq + 1) throw new ApiError(0, 'input_uncertain', 'The terminal input acknowledgement was not valid. Input was stopped.')
    return { input_seq: seq + 1 }
  }
  async resize(terminal: string, cols: number, rows: number): Promise<void> {
    const path = this.terminal(terminal)
    if (!integer(cols, 1, 1000) || !integer(rows, 1, 500)) throw bad()
    const v = await this.request('POST', `${path}/resize`, { cols, rows })
    if (!record(v) || v.kind !== 'resized' || !keys(v, ['kind', 'size']) || !record(v.size) || !keys(v.size, ['cols', 'rows']) || v.size.cols !== cols || v.size.rows !== rows) throw bad()
  }
  async close(terminal: string): Promise<void> {
    const v = await this.request('DELETE', this.terminal(terminal))
    if (!record(v) || v.kind !== 'closed' || !keys(v, ['kind'])) throw bad()
  }
  watch(terminal: string, after: number, handle: (event: TerminalEvent) => void): () => void {
    let path: string
    try { path = this.terminal(terminal); if (!integer(after)) throw bad() }
    catch (error) {
      let cancelled = false
      queueMicrotask(() => { if (!cancelled) handle({type: 'stopped', message: error instanceof Error ? error.message : 'The terminal output connection could not open.'}) })
      return () => { cancelled = true }
    }
    let cursor = after
    let socket: WebSocket | null = null
    let stopped = false
    let ended = false
    let pending = false
    let finalPending = false
    let reconnect = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const deliver = (event: TerminalEvent) => { if (!stopped && this.valid()) handle(event) }
    // `refused`: the server refused this watch or what it sent, and would again; nothing reconnects by itself.
    const error = (message: string, gone = false, refused = false) => deliver({ type: 'stopped', message, gone, ...(refused ? { refused } : {}) })
    const schedule = () => {
      if (!stopped && !ended && !pending && reconnect && timer === null && this.valid()) timer = setTimeout(() => { timer = null; connect() }, this.options.reconnectMs ?? 1000)
    }
    const connect = () => {
      if (stopped || ended || !this.valid()) return
      reconnect = false
      const protocol = this.page.protocol === 'https:' ? 'wss:' : 'ws:'
      let ws: WebSocket
      try { ws = new this.Socket(`${protocol}//${this.page.host}${path}/stream?after=${cursor}&as=${encodeURIComponent(this.binding.member)}`) }
      catch { error('The terminal connection was lost. Input was stopped.'); reconnect = true; schedule(); return }
      socket = ws
      const live = () => !stopped && !ended && socket === ws && this.valid()
      const invalid = () => { ended = true; error('The terminal server sent an invalid output message.', false, true); ws.close(1008) }
      ws.onmessage = (event: MessageEvent) => {
        if (!live()) return
        if (typeof event.data !== 'string' || event.data.length > MAX_FRAME || pending) return invalid()
        let raw: unknown
        try { raw = JSON.parse(event.data) as unknown } catch { return invalid() }
        if (record(raw) && raw.kind === 'refused' && keys(raw, ['kind', 'reason']) && text(raw.reason, 64)) { ended = true; error('The terminal server refused the output connection.', false, true); ws.close(1008); return }
        const frame = terminalOutput(raw)
        if (!frame) return invalid()
        pending = true
        finalPending = frame.end !== undefined
        if (frame.reset) deliver({ type: 'reset' })
        let consumed = false
        deliver({ type: 'data', offset: frame.offset, data: frame.data, consumed: () => {
          if (consumed) return
          consumed = true
          const after = frame.offset + frame.data.length
          cursor = frame.reset ? after : Math.max(cursor, after)
          pending = false
          if (!live()) return
          if (frame.end) {
            ended = true
            if (frame.end.kind === 'exited') deliver({ type: 'exit', code: frame.end.code })
            else error(frame.end.kind === 'closed' ? 'The terminal was closed.' : 'The terminal stopped after an I/O failure.', frame.end.kind === 'closed')
          } else if (ws.readyState === 1) {
            try { ws.send(JSON.stringify({ after })) } catch { error('The terminal connection was lost. Input was stopped.'); reconnect = true; ws.close(); }
          }
          schedule()
        } })
      }
      ws.onclose = (event: CloseEvent) => {
        if (!live() || ended) return
        if (event.code === 4401) { ended = true; error('The terminal sign-in ended. Input was stopped.', false, true); this.binding.unauthorized(); return }
        // A final piece may still be drawing after the normal close arrived.
        if (event.code === 1000 && finalPending) return
        if (event.code === 1008) { ended = true; error('The terminal output connection was refused.', false, true); return }
        error('The terminal connection was lost. Input was stopped.')
        reconnect = true
        schedule()
      }
    }
    const stop = () => { if (stopped) return; stopped = true; if (timer !== null) clearTimeout(timer); socket?.close(); this.watches.delete(stop) }
    this.watches.add(stop)
    connect()
    return stop
  }
  dispose(): void {
    if (this.over) return
    this.over = true
    for (const controller of this.controllers) controller.abort()
    this.controllers.clear()
    for (const stop of [...this.watches]) stop()
    releaseTerminalClient(this)
  }
}
